import React, { useRef, useMemo, useCallback, useEffect } from 'react';
import { Chessboard, fenStringToPositionObject } from 'react-chessboard';
import { Chess } from 'chess.js';
import { playChessMoveSound, playChessCaptureSound } from '@/lib/chessSounds';

// ═══════════════════════════════════════════════════════════════════════════
// [EXP-1.4] Ocultar la pieza fantasma (opacity 0.5 por defecto de la librería)
// ═══════════════════════════════════════════════════════════════════════════
// react-chessboard@5.12 renderiza en cada casilla con pieza:
//   1. Un <Piece> normal en la casilla original — durante el drag recibe
//      `defaultDraggingPieceGhostStyle = { opacity: 0.5 }` → percibida como
//      pieza "fantasma / sombreada / deslavada".
//   2. Un <Piece clone> dentro del <DragOverlay> — la copia visible que
//      sigue al cursor, escalada a 1.2×.
//
// La librería expone el prop público `draggingPieceGhostStyle` (ver
// ChessboardProvider.d.ts:81) para SOBRESCRIBIR ese estilo. Aquí lo
// sobrescribimos con `opacity: 0` → la pieza original queda invisible
// durante el drag; al soltar (válido o inválido) el estado `draggingPiece`
// vuelve a null, la condición del estilo ghost deja de cumplirse y la
// pieza vuelve a mostrarse con opacidad 1 (o queda en la nueva casilla si
// el drop fue válido). Cero cambio a la lógica de drop, snap-back,
// animación o sincronización.
//
// Reversión: cambiar EXP_HIDE_ORIGINAL_ON_DRAG a false y todo vuelve al
// comportamiento anterior sin borrar código.
// ═══════════════════════════════════════════════════════════════════════════
const EXP_HIDE_ORIGINAL_ON_DRAG = false;

/**
 * ChessBoardView — chess.com-style right-click annotation layer on top of react-chessboard v5.
 *
 *  ARROWS   (right-click + drag from A -> B):
 *     no modifier -> yellow
 *     Ctrl        -> red
 *     Alt         -> blue
 *     Shift       -> green
 *
 *  SQUARES  (right-click on a single square — no drag):
 *     no modifier -> red
 *     Ctrl        -> yellow
 *     Alt         -> blue
 *     Shift       -> green
 *
 *  Repeating the same action (same arrow OR same square) with the SAME color removes it (toggle-off).
 *  Repeating with a DIFFERENT color replaces the previous mark on that path/square.
 *
 *  Annotations are pure visual overlays — they NEVER trigger a chess move, mutate the FEN,
 *  or interact with the exercise validator. Left-click drag continues to work for piece moves.
 */

const COLORS = {
  yellow: 'rgba(234, 179, 8, 0.85)',
  orange: 'rgba(234, 179, 8, 0.85)', // legacy alias -> yellow
  green:  'rgba(34, 160, 84, 0.85)',
  blue:   'rgba(37, 99, 235, 0.85)',
  red:    'rgba(220, 38, 38, 0.85)',
};
// Softer variant used to fill the whole square as a transparent overlay so the
// piece and coordinates remain readable underneath.
const HIGHLIGHT_COLORS = {
  yellow: 'rgba(234, 179, 8, 0.55)',
  orange: 'rgba(234, 179, 8, 0.55)',
  green:  'rgba(34, 160, 84, 0.55)',
  blue:   'rgba(37, 99, 235, 0.50)',
  red:    'rgba(220, 38, 38, 0.50)',
};
const resolveColor = (c) => COLORS[c] || c || COLORS.yellow;
const resolveHighlight = (c) => HIGHLIGHT_COLORS[c] || COLORS[c] || COLORS.yellow;

const FILES = 'abcdefgh';

const pointToSquare = (x, y, size, orientation) => {
  const cell = size / 8;
  const col = Math.floor(x / cell);
  const row = Math.floor(y / cell);
  if (col < 0 || col > 7 || row < 0 || row > 7) return null;
  if (orientation === 'white') return FILES[col] + String(8 - row);
  return FILES[7 - col] + String(row + 1);
};

const squareToCenter = (sq, size, orientation) => {
  const cell = size / 8;
  const file = FILES.indexOf(sq[0]);
  const rank = parseInt(sq[1], 10);
  const col = orientation === 'white' ? file : 7 - file;
  const row = orientation === 'white' ? 8 - rank : rank - 1;
  return { cx: (col + 0.5) * cell, cy: (row + 0.5) * cell };
};

// Color mapping based on modifier keys (chess.com convention)
const colorForModifier = (e, isSquare) => {
  if (e.shiftKey) return 'green';
  if (e.altKey) return 'blue';
  if (e.ctrlKey || e.metaKey) return isSquare ? 'yellow' : 'red';
  return isSquare ? 'red' : 'yellow';
};

function ChessBoardView({
  fen,
  onPieceDrop,
  onSquareClick,
  orientation = 'white',
  boardWidth = 520,
  arePiecesDraggable = true,
  allowAnnotations = false,
  allowLeftClickAnnotations = false,
  conventionalLiveAnnotations = false,
  rightClickArrowOnly = false,
  arrows = [],
  circles = {},
  extraSquareStyles = {},
  captureSquares = null, // optional Set<string> — targets counted as capture
                         // for FENs chess.js cannot parse (Capture Activity)
  suppressSounds = false, // optional — when true, the caller takes full
                         // control of audio (used by Capture Activity so it
                         // can play its custom collect / final-collect
                         // sounds without doubling with the generic chess
                         // move/capture cues).
  onAnnotationsChange,
  onArrowRightClick,
  clearAnnotationsOnRightClick = false,
  clearAnnotationsOnLeftClick = false,
  // Fix #4 — cuando true, la próxima transición de `fen` se aplica sin
  // animación (animationDurationInMs: 0). El padre lo activa sólo para
  // cambios bruscos de posición (ej. Load Game) y lo desactiva justo
  // después, preservando la animación normal de las jugadas de la clase.
  disableAnimation = false,
  // Paso 1.1 (Fix perf drag pickup) — callback opcional que notifica al
  // padre cuando el drag empieza (true) o termina (false, ya sea por drop
  // válido, drop inválido, cancel/escape, o safety-release). El padre lo
  // usa para pausar polling durante el drag y evitar re-renders que
  // reseteen el sensor @dnd-kit interno de react-chessboard.
  // Cero cambios funcionales al pipeline de movimiento — es puramente una
  // señal externa.
  onDragActiveChange,
}) {
  const wrapRef = useRef(null);
  const boardRef = useRef(null);
  const downRef = useRef(null);



  // ═══════════════════════════════════════════════════════════════════════
  // Paso 1.1 — Notificación de drag activo al padre
  // ═══════════════════════════════════════════════════════════════════════
  // Objetivo: exponer una señal `active: boolean` al padre para que pueda
  // suspender el polling mientras hay un drag en curso. Esto evita que un
  // re-render provocado por `fetchRoom` resetee el sensor de @dnd-kit
  // durante la fase de activation del drag ("seleccionar 2 veces" +
  // sombreado extraño reportados por el profesor).
  //
  // Garantías de liberación (obligatorias por el brief):
  //   1. Drop válido       → onPieceDrop (wrap en handlePieceDrop)  → false
  //   2. Drop inválido     → onPieceDrop (retorna false)            → false
  //   3. Drop fuera        → onPieceDragCancel                      → false
  //   4. Cancelación ESC   → onPieceDragCancel                      → false
  //   5. Interrupción      → safety timer (SAFETY_MS)               → false
  //   6. Unmount           → cleanup useEffect                      → false
  //
  // Sin setState — sólo ref + timer. Cero renders extra.
  // ═══════════════════════════════════════════════════════════════════════
  const onDragActiveChangeRef = useRef(onDragActiveChange);
  useEffect(() => { onDragActiveChangeRef.current = onDragActiveChange; }, [onDragActiveChange]);
  const dragActiveInternalRef = useRef(false);
  const dragSafetyTimerRef = useRef(null);
  const DRAG_SAFETY_MS = 5000; // Failsafe: el drag NUNCA queda activo >5s

  const notifyDragActive = useCallback((active) => {
    // Idempotente: evita disparar el callback dos veces con el mismo estado.
    if (dragActiveInternalRef.current === active) return;
    dragActiveInternalRef.current = active;
    // Safety timer: si `active=true` armamos un timer que fuerza `false`
    // después de 5s (protege contra cualquier callback perdido).
    if (dragSafetyTimerRef.current) {
      clearTimeout(dragSafetyTimerRef.current);
      dragSafetyTimerRef.current = null;
    }
    if (active) {
      dragSafetyTimerRef.current = setTimeout(() => {
        if (dragActiveInternalRef.current) {
          dragActiveInternalRef.current = false;
          try { onDragActiveChangeRef.current?.(false); } catch (_) {}
        }
        dragSafetyTimerRef.current = null;
      }, DRAG_SAFETY_MS);
    }
    try { onDragActiveChangeRef.current?.(active); } catch (_) {}
  }, []);

  // Cleanup en unmount: libera el flag y limpia el timer para evitar que el
  // padre quede con polling suspendido si el componente desaparece durante
  // un drag activo (raro pero posible al desmontar la vista de clase).
  useEffect(() => {
    return () => {
      if (dragSafetyTimerRef.current) {
        clearTimeout(dragSafetyTimerRef.current);
        dragSafetyTimerRef.current = null;
      }
      if (dragActiveInternalRef.current) {
        dragActiveInternalRef.current = false;
        try { onDragActiveChangeRef.current?.(false); } catch (_) {}
      }
    };
  }, []);

  // Live refs let handlers stay stable across re-renders — without this the
  // window.mouseup listener detached & re-attached on every parent render
  // (~30x/min from polling alone), and the Chessboard's `options` memo was
  // invalidated whenever the parent re-rendered because callback identities
  // changed. Handlers now always read the freshest values via *Ref.current
  // without needing them as useCallback deps.
  const arrowsRef = useRef(arrows);
  const circlesRef = useRef(circles);
  const onAnnotationsChangeRef = useRef(onAnnotationsChange);
  useEffect(() => { arrowsRef.current = arrows; }, [arrows]);
  useEffect(() => { circlesRef.current = circles; }, [circles]);
  useEffect(() => { onAnnotationsChangeRef.current = onAnnotationsChange; }, [onAnnotationsChange]);

  const commit = useCallback((next) => {
    onAnnotationsChangeRef.current?.(next.arrows, next.circles);
  }, []);

  const toggleSquare = useCallback((sq, color, removeRegardlessOfColor = false) => {
    const src = circlesRef.current || {};
    const nextCircles = { ...src };
    if (
      Object.prototype.hasOwnProperty.call(nextCircles, sq) &&
      (removeRegardlessOfColor || nextCircles[sq] === color)
    ) {
      delete nextCircles[sq];
    } else {
      nextCircles[sq] = color;
    }
    commit({ arrows: arrowsRef.current || [], circles: nextCircles });
  }, [commit]);

  const toggleArrow = useCallback((from, to, color, removeRouteRegardlessOfColor = false) => {
    const src = arrowsRef.current || [];
    const sameIdx = src.findIndex(a =>
      a[0] === from && a[1] === to &&
      (removeRouteRegardlessOfColor || a[2] === color)
    );
    if (sameIdx >= 0) {
      const next = src.slice(); next.splice(sameIdx, 1);
      commit({ arrows: next, circles: circlesRef.current || {} });
      return;
    }
    const next = src.filter(a => !(a[0] === from && a[1] === to));
    next.push([from, to, color]);
    commit({ arrows: next, circles: circlesRef.current || {} });
  }, [commit]);

  const handlePieceDrop = useCallback(({ sourceSquare, targetSquare }) => {
    if (!onPieceDrop || !sourceSquare || !targetSquare) return false;
    // Detect capture BEFORE the drop resolves. Priority order:
    //   1. Caller-supplied captureSquares Set — used by Capture Activity
    //      whose FEN lacks a king so chess.js can't parse it.
    //   2. chess.js on the current fen prop — piece at destination OR pawn
    //      diagonal onto an empty square (en passant).
    // Any failure silently falls back to the plain move sound. Sound
    // dispatch is fire-and-forget so an audio glitch never blocks the move.
    let wasCapture = false;
    if (captureSquares && typeof captureSquares.has === 'function' && captureSquares.has(targetSquare)) {
      wasCapture = true;
    } else if (fen) {
      try {
        const c = new Chess(fen);
        const dstPiece = c.get(targetSquare);
        if (dstPiece) {
          wasCapture = true;
        } else {
          const srcPiece = c.get(sourceSquare);
          if (srcPiece && srcPiece.type === 'p' && sourceSquare[0] !== targetSquare[0]) {
            wasCapture = true;
          }
        }
      } catch { /* not a legal FEN — fall back to move sound */ }
    }
    const ok = !!onPieceDrop(sourceSquare, targetSquare);
    if (ok && !suppressSounds) (wasCapture ? playChessCaptureSound : playChessMoveSound)();
    // Paso 1.1 — libera el flag de drag activo tras un drop (válido o no).
    // Se ejecuta ANTES de retornar para que el próximo tick de polling
    // (que corre a los ~2s) ya vea el drag como inactivo.
    notifyDragActive(false);
    return ok;
  }, [onPieceDrop, fen, captureSquares, suppressSounds, notifyDragActive]);

  // Paso 1.1 — handlers para el ciclo de vida del drag. `onPieceDrag` se
  // dispara cuando el drag se activa realmente (después de la
  // activation-constraint de @dnd-kit). `onPieceDragCancel` cubre drop
  // fuera del tablero, tecla Escape, y cancelaciones internas.
  const handlePieceDragBegin = useCallback(() => {
    notifyDragActive(true);
  }, [notifyDragActive]);
  const handlePieceDragCancel = useCallback(() => {
    notifyDragActive(false);
  }, [notifyDragActive]);




  // ------------------------------ mouse handlers ------------------------------
  const DRAG_THRESHOLD_PX = 6;

  const onMouseDown = useCallback((e) => {
    if (!allowAnnotations || e.button !== 2) return;
    const el = boardRef.current || wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const sq = pointToSquare(e.clientX - rect.left, e.clientY - rect.top, boardWidth, orientation);
    if (!sq) { downRef.current = null; return; }
    downRef.current = {
      sq,
      x: e.clientX,
      y: e.clientY,
      dragged: false,
      mods: { shiftKey: e.shiftKey, altKey: e.altKey, ctrlKey: e.ctrlKey, metaKey: e.metaKey },
    };
  }, [allowAnnotations, boardWidth, orientation]);

  const onMouseMove = useCallback((e) => {
    const d = downRef.current;
    if (!d || d.dragged) return;
    const dx = e.clientX - d.x, dy = e.clientY - d.y;
    if ((dx * dx + dy * dy) > (DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX)) {
      d.dragged = true;
    }
  }, []);

  const onMouseUp = useCallback((e) => {
    if (!allowAnnotations || e.button !== 2) return;
    const d = downRef.current;
    downRef.current = null;
    if (!d) return;
    const el = boardRef.current || wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const sq = pointToSquare(e.clientX - rect.left, e.clientY - rect.top, boardWidth, orientation);
    // Use modifier state captured at mousedown so a slow key release doesn't change intent
    const mods = d.mods;
    const hasMods = mods.shiftKey || mods.altKey || mods.ctrlKey || mods.metaKey;
    // A gesture is a DRAG only if the pointer moved past the threshold.
    // Releasing on the same square without exceeding the threshold is a CLICK.
    if (d.dragged && sq && sq !== d.sq) {
      toggleArrow(d.sq, sq, colorForModifier(mods, false), rightClickArrowOnly);
      return;
    }
    // Sloppy drag that ended inside the origin square — treat as no-op to avoid
    // accidentally wiping annotations when the user intended to draw an arrow.
    if (d.dragged) return;
    // Fuera del modo convencional, se conserva el comportamiento anterior.
    // En Explanation Live, el clic derecho también puede marcar casillas.
    if (rightClickArrowOnly && !conventionalLiveAnnotations) return;
    // Piso es Lava: un clic derecho sin arrastrar limpia todas las anotaciones.
    // Esta opción sólo se activa explícitamente desde ese módulo.
    if (
      clearAnnotationsOnRightClick &&
      (
        (arrowsRef.current || []).length > 0 ||
        Object.keys(circlesRef.current || {}).length > 0
      )
    ) {
      commit({ arrows: [], circles: {} });
      return;
    }

    // Comportamiento habitual: marcar o desmarcar una casilla.
    toggleSquare(
      d.sq,
      colorForModifier(mods, true),
      conventionalLiveAnnotations
    );
  }, [
    allowAnnotations,
    boardWidth,
    orientation,
    toggleSquare,
    toggleArrow,
    clearAnnotationsOnRightClick,
    rightClickArrowOnly,
    conventionalLiveAnnotations,
    commit
  ]);

  const onContextMenu = useCallback((e) => {
    if (allowAnnotations) e.preventDefault();
  }, [allowAnnotations]);

  // Attach a window mouseup so drags that end OUTSIDE the board still commit
  useEffect(() => {
    if (!allowAnnotations) return;
    const up = (e) => {
      if (e.button !== 2) return;
      if (!downRef.current) return;
      onMouseUp(e);
    };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, [allowAnnotations, onMouseUp]);

  // ------------------------------ styles / options ------------------------------
  const squareStyles = useMemo(() => {
    const out = { ...(extraSquareStyles || {}) };
    Object.entries(circles || {}).forEach(([sq, c]) => {
      const col = resolveHighlight(c);
      // Full-square semi-transparent overlay (annotation wins over other bg like
      // last-move tint). Smooth fade so the highlight appears and disappears
      // pleasantly instead of snapping.
      out[sq] = {
        ...(out[sq] || {}),
        backgroundColor: col,
        transition: 'background-color 150ms ease',
      };
    });
    return out;
  }, [circles, extraSquareStyles]);

  const handleSquareClick = useCallback(({ square }) => {
    if (conventionalLiveAnnotations || clearAnnotationsOnLeftClick) {
      if (
        (arrowsRef.current || []).length > 0 ||
        Object.keys(circlesRef.current || {}).length > 0
      ) {
        commit({ arrows: [], circles: {} });
      }
      return;
    }
    if (allowLeftClickAnnotations && square) {
      const nextCircles = { ...(circlesRef.current || {}) };
      if (Object.prototype.hasOwnProperty.call(nextCircles, square)) {
        delete nextCircles[square];
      } else {
        nextCircles[square] = 'red';
      }
      commit({ arrows: arrowsRef.current || [], circles: nextCircles });
      return;
    }
    if (onSquareClick && square) onSquareClick(square);
  }, [
    onSquareClick,
    allowLeftClickAnnotations,
    conventionalLiveAnnotations,
    clearAnnotationsOnLeftClick,
    commit
  ]);

  // Pre-parsear FEN → objeto de posición UNA sola vez cuando `fen` cambia.
  // Evita que ChessboardProvider (línea 4871/4890/4982 de la librería) haga
  // fenStringToPositionObject tres veces por ciclo de reconciliación.
  const positionObj = useMemo(() => {
    if (!fen) return {};
    try { return fenStringToPositionObject(fen, 8, 8); } catch { return {}; }
  }, [fen]);

  const options = useMemo(() => {
    const opts = {
      position: positionObj,
      boardOrientation: orientation,
      allowDragging: !!arePiecesDraggable,
      onPieceDrop: handlePieceDrop,
      // Paso 1.1 — señal de drag para pausa de polling en el padre.
      // Sólo se conectan si arePiecesDraggable (para el alumno son no-ops
      // porque react-chessboard no dispara callbacks de drag cuando el
      // dragging está deshabilitado — su polling nunca se pausa).
      onPieceDrag: arePiecesDraggable ? handlePieceDragBegin : undefined,
      onPieceDragCancel: arePiecesDraggable ? handlePieceDragCancel : undefined,
      onSquareClick:
        (onSquareClick || clearAnnotationsOnLeftClick ||
         allowLeftClickAnnotations || conventionalLiveAnnotations)
          ? handleSquareClick
          : undefined,
      boardStyle: { borderRadius: 0, width: boardWidth, height: boardWidth },
      darkSquareStyle: { backgroundColor: '#B58863' },
      lightSquareStyle: { backgroundColor: '#F0D9B5' },
      squareStyles,
      // [EXP-1.4] Ocultar la pieza fantasma (opacity 0.5 de la librería) durante
      // el drag. Sobreescribe defaultDraggingPieceGhostStyle vía prop público.
      // Sin efecto si arePiecesDraggable=false (alumnos y monitores) porque la
      // librería nunca activa el estado ghost.
      draggingPieceGhostStyle: EXP_HIDE_ORIGINAL_ON_DRAG ? { opacity: 0 } : undefined,
      // We render our own arrows via SVG so we can drive them with keyboard modifiers.
      // Disable the library's built-in drawing entirely to avoid double-handling.
      allowDrawingArrows: false,
      animationDurationInMs: disableAnimation ? 0 : 180,
      showAnimations: true,
    };
    return opts;
  }, [positionObj, orientation, arePiecesDraggable, handlePieceDrop, handlePieceDragBegin, handlePieceDragCancel, handleSquareClick, onSquareClick, clearAnnotationsOnLeftClick, allowLeftClickAnnotations, conventionalLiveAnnotations, boardWidth, squareStyles, disableAnimation]);

  // ------------------------------ SVG arrows ------------------------------
  const renderedArrows = useMemo(() => {
    if (!arrows || arrows.length === 0) {
      return null;
    }
    const cell = boardWidth / 8;
    const strokeW = Math.max(4, cell * 0.16);
    const _rendered = arrows.map((a, i) => {
      const [fromSq, toSq, colorKey] = a;
      const from = squareToCenter(fromSq, boardWidth, orientation);
      const to   = squareToCenter(toSq, boardWidth, orientation);
      const col  = resolveColor(colorKey);
      const markerId = `c4k-ah-${i}-${colorKey}`;

      // Knight-move detection in FILE/RANK space (orientation-independent)
      const dFile = Math.abs(FILES.indexOf(toSq[0]) - FILES.indexOf(fromSq[0]));
      const dRank = Math.abs(parseInt(toSq[1], 10) - parseInt(fromSq[1], 10));
      const isKnight = (dFile === 1 && dRank === 2) || (dFile === 2 && dRank === 1);

      // Common start offset so the tail doesn't cover the source piece
      const startInset = cell * 0.15;
      // Common end offset so the arrowhead tip lands close to target center
      const endShorten = cell * 0.35;

      let d;           // SVG path
      let strokeLinejoin = 'round';

      if (isKnight) {
        // L-shape: travel the LONG axis first (2 squares), then turn to the target
        // (this matches chess.com's visual convention)
        const dxTotal = to.cx - from.cx;
        const dyTotal = to.cy - from.cy;
        const goHorizFirst = dFile === 2; // long axis is horizontal
        const elbow = goHorizFirst
          ? { x: to.cx, y: from.cy }
          : { x: from.cx, y: to.cy };

        // Nudge the starting point away from the source center
        const startDir = goHorizFirst
          ? { x: Math.sign(dxTotal), y: 0 }
          : { x: 0, y: Math.sign(dyTotal) };
        const sx = from.cx + startDir.x * startInset;
        const sy = from.cy + startDir.y * startInset;

        // Nudge the ending point back from the target center
        const endDir = goHorizFirst
          ? { x: 0, y: Math.sign(dyTotal) }
          : { x: Math.sign(dxTotal), y: 0 };
        const ex = to.cx - endDir.x * endShorten;
        const ey = to.cy - endDir.y * endShorten;

        d = `M ${sx} ${sy} L ${elbow.x} ${elbow.y} L ${ex} ${ey}`;
      } else {
        const dx = to.cx - from.cx;
        const dy = to.cy - from.cy;
        const len = Math.hypot(dx, dy) || 1;
        const ux = dx / len, uy = dy / len;
        const sx = from.cx + ux * startInset;
        const sy = from.cy + uy * startInset;
        const ex = to.cx - ux * endShorten;
        const ey = to.cy - uy * endShorten;
        d = `M ${sx} ${sy} L ${ex} ${ey}`;
      }

      return (
        <g key={`arr-${fromSq}-${toSq}-${colorKey}-${i}`}>
          <defs>
            <marker id={markerId} markerWidth="3.5" markerHeight="3.5" refX="1.7" refY="1.75" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L3.5,1.75 L0,3.5 z" fill={col} />
            </marker>
          </defs>
          {onArrowRightClick && (
            <path
              d={d}
              fill="none"
              stroke="transparent"
              strokeWidth={Math.max(18, cell * 0.35)}
              pointerEvents="stroke"
              onContextMenu={event => {
                event.preventDefault();
                event.stopPropagation();
                onArrowRightClick(fromSq, toSq, colorKey);
              }}
            />
          )}
          <path
            d={d}
            fill="none"
            stroke={col}
            strokeWidth={strokeW}
            pointerEvents="none"
            strokeLinecap="round"
            strokeLinejoin={strokeLinejoin}
            markerEnd={`url(#${markerId})`}
            opacity="0.95"
          />
        </g>
      );
    });
    return _rendered;
  }, [arrows, boardWidth, orientation, onArrowRightClick]);

  // Remount forzado del <Chessboard> ÚNICAMENTE en Ejercicios de Base de Datos.
  // Objetivo: eliminar el 1-frame gap del useEffect [position] de la librería
  // haciendo que `currentPosition` se inicialice síncronamente via useState en
  // el primer render tras cambiar `fen`.
  // Discriminación DB Exercise vs Capture Activity sin nuevos props (parent
  // no puede modificarse en esta prueba):
  //   - DB Exercise: posición legal de ajedrez → contiene wK y bK.
  //   - Capture Activity: FEN sin reyes (student piece + targets).
  //   - Classroom normal: disableAnimation=false → key estable.
  // [FIX-DB-REMOUNT] key SIEMPRE 'stable' → nunca se remonta <Chessboard>.
  // El remount por-FEN (antes activo solo en Base de Datos) causaba 2–3 s de
  // retraso por movimiento al recrear @dnd-kit + sprites. Con
  // animationDurationInMs:0 la librería aplica la posición síncronamente
  // (setCurrentPosition) sin necesidad de remontar. Capture Activities
  // ya usaba 'stable', así que su comportamiento queda 1:1.
  const chessKey = 'stable';

  return (
    <div
      ref={wrapRef}
      className="rounded-2xl overflow-hidden chess-shadow border border-slate-200 bg-white select-none"
      style={{ width: boardWidth }}
      data-testid="chessboard-view"
    >
      <div
        ref={boardRef}
        className="relative"
        style={{ width: boardWidth, height: boardWidth }}
        onContextMenu={onContextMenu}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
      >
        <Chessboard key={chessKey} options={options} />
        {renderedArrows && (
          <svg
            width={boardWidth}
            height={boardWidth}
            className="absolute top-0 left-0"
            style={{ pointerEvents: onArrowRightClick ? 'auto' : 'none' }}
            data-testid="chessboard-arrows"
          >
            {renderedArrows}
          </svg>
        )}
      </div>
    </div>
  );
}

// Wrap in React.memo so the board is skipped when the parent re-renders without
// any prop actually changing (very common in Classroom due to polling that
// refreshes participants / chat / ranking without touching board props). Uses
// the default shallow compare — identity-changing props (new arrays/objects
// from parents) still trigger renders exactly as before, so behavior is
// preserved: no changes to drag & drop, annotations, arrows, colors, sounds,
// promotion, or engine wiring.
export default React.memo(ChessBoardView);
